import { useEffect, useMemo, useState } from 'react'

import { F0Alert, F0Box, F0Dialog, F0Select } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { formatHoursCompleted } from 'modules/trainings/lib/utils'

type Props = {
  open: boolean
  onClose: () => void
  sessionDurationDecimal: number
  initialCompletedDurationDecimal: number
  onSave: (completedDurationDecimal: number) => Promise<void>
  saving: boolean
}

const toDecimalHours = (hours: number, minutes: number) => hours + minutes / 60

const buildHourOptions = (maxHours: number) =>
  Array.from({ length: maxHours + 1 }, (_, i) => ({
    label: String(i),
    value: String(i),
  }))

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({
  label: String(i),
  value: String(i),
}))

export const ModifyHoursModal = ({
  open,
  onClose,
  sessionDurationDecimal,
  initialCompletedDurationDecimal,
  onSave,
  saving,
}: Props) => {
  const initial = initialCompletedDurationDecimal
  const [hours, setHours] = useState(Math.floor(initial))
  const [minutes, setMinutes] = useState(Math.round((initial % 1) * 60))

  useEffect(() => {
    if (open) {
      setHours(Math.floor(initial))
      setMinutes(Math.round((initial % 1) * 60))
    }
  }, [open, initial])
  const decimalHours = toDecimalHours(hours, minutes)
  const isValid =
    decimalHours >= 0 && (sessionDurationDecimal === 0 || decimalHours <= sessionDurationDecimal)

  const maxHours = Math.ceil(sessionDurationDecimal) || 23
  const hourOptions = useMemo(() => buildHourOptions(maxHours), [maxHours])

  const handleSubmit = async () => {
    if (!isValid) return
    await onSave(decimalHours)
  }

  if (!open) return null

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('trainings.modify_hours.title')}
      primaryAction={{
        label: i18n.t('trainings.modify_hours.save'),
        onClick: handleSubmit,
        loading: saving,
        disabled: !isValid,
      }}
      secondaryAction={{
        label: i18n.t('trainings.modify_hours.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="2xl">
        {sessionDurationDecimal > 0 && (
          <F0Alert
            variant="info"
            title={i18n.t('trainings.modify_hours.alert_title')}
            description={i18n.t('trainings.modify_hours.alert_description', {
              duration: formatHoursCompleted(sessionDurationDecimal),
            })}
          />
        )}
        <F0Box display="flex" flexDirection="row" gap="lg">
          <F0Box grow>
            <F0Select
              label={i18n.t('trainings.modify_hours.hours')}
              value={String(hours)}
              options={hourOptions}
              onChange={(value: string) => setHours(parseInt(value, 10) || 0)}
            />
          </F0Box>
          <F0Box grow>
            <F0Select
              label={i18n.t('trainings.modify_hours.mins')}
              value={String(minutes)}
              options={MINUTE_OPTIONS}
              onChange={(value: string) => setMinutes(parseInt(value, 10) || 0)}
            />
          </F0Box>
        </F0Box>
      </F0Box>
    </F0Dialog>
  )
}
